import type { CancellationReason } from './cancellation';
import { ofertaVigente } from './plans';
import { pausaPendiente } from './pausa';
import type { BillingInterval, PlanId } from './types';

/**
 * Retención al darse de baja (Pol, 18 oct 2026; la idea es el flujo de Uber One).
 *
 * «Cancelar mi plan» abre como mucho dos pantallas antes de poder cancelar:
 *
 *   1. ¿Por qué te vas? (los 6 motivos + «Cuéntanos más»).
 *   2. Una sola oferta, según el motivo, si la cuenta puede recibirla.
 *   3. Confirmar: qué pierde y hasta cuándo lo mantiene → portal de Stripe.
 *
 * Reglas puras que comparten la API (routes/billing.ts + retencionBaja.ts) y la
 * demo de la web, para que las dos ofrezcan lo mismo a la misma cuenta.
 */

/** Cupones de Stripe con id fijo (los crea infra/scripts/stripe-precios.mjs). */
export const RETENCION_CUPONES = {
  /** 50 % durante 3 meses, `repeating`. Distinto del de lanzamiento. */
  descuento: 'alexia-retencion-50-3m',
  /** 100 % un mes, para quien acepta que le dejemos Alexia montada. */
  mesGratis: 'alexia-retencion-mes-gratis',
} as const;

export const RETENCION_DESCUENTO = { percent: 50, months: 3 } as const;

/** La pausa es de 1 o 2 meses, contados desde el fin del periodo ya pagado. */
export type MesesDePausa = 1 | 2;
export const MESES_DE_PAUSA: readonly MesesDePausa[] = [1, 2];

/** Una oferta por cuenta cada 12 meses. */
export const DIAS_ENTRE_OFERTAS_DE_RETENCION = 365;

/** Registro de cada oferta enseñada: `RETENCIONOFERTA#<ISO>` en el tenant. */
export const PREFIJO_OFERTA_RETENCION = 'RETENCIONOFERTA#';

/** La oferta que se enseña en la pantalla 2. */
export type OfertaRetencion = 'descuento' | 'pausa' | 'ayuda' | 'falta';
/** Lo que se puede aceptar en ella. */
export type AceptacionRetencion = 'descuento' | 'basico' | 'pausa' | 'ayuda';

/**
 * Una sola oferta según el motivo:
 * - `price` → 50 % durante 3 meses (y, si está en Pro, pasar a Básico).
 * - `time` y `other` → pausa de 1 o 2 meses.
 * - `difficult` → te la dejamos montada (llamada de 20 min + un mes gratis).
 * - `missing` → «¿Qué te falta?» y la pausa mientras.
 * - `closed` → sin oferta.
 */
export function ofertaParaMotivo(reason: CancellationReason): OfertaRetencion | null {
  switch (reason) {
    case 'price': return 'descuento';
    case 'time':
    case 'other': return 'pausa';
    case 'difficult': return 'ayuda';
    case 'missing': return 'falta';
    default: return null;
  }
}

/** Qué se puede aceptar con cada oferta. Pasar a Básico, solo desde Pro. */
export function aceptacionesDe(oferta: OfertaRetencion, plan: PlanId): AceptacionRetencion[] {
  if (oferta === 'descuento') return plan === 'pro' ? ['descuento', 'basico'] : ['descuento'];
  if (oferta === 'ayuda') return ['ayuda'];
  return ['pausa'];
}

/** Por qué una cuenta no recibe oferta (paso 1 → paso 3). */
export type SinOfertaPor =
  | 'cortesia'
  | 'sin_suscripcion'
  | 'impago'
  | 'anual'
  | 'founder'
  | 'en_pausa'
  | 'baja_programada'
  | 'oferta_activa'
  | 'ya_tuvo_oferta';

export interface TenantParaOfertaDeBaja {
  plan: PlanId;
  subscriptionStatus: string;
  billingInterval?: BillingInterval;
  stripeSubscriptionId?: string;
  offer?: { code: string; startedAt: string };
  cancelAt?: string;
  pausedFrom?: string;
  pausedUntil?: string;
}

/**
 * ¿Por qué NO se le ofrece nada? `null` = sí puede recibir una oferta.
 *
 * Sin ofertas: cortesía, sin suscripción viva, impago (`past_due`), plan
 * ANUAL (decisión de Pol: su baja es «no renovar»), Founder (ya tiene precio
 * especial; el 50 % encima de 39 lo dejaría en menos de lo que cuesta
 * atenderle), una pausa ya puesta, una baja ya programada, un cupón u oferta
 * activa (lanzamiento, ALEXFAMILY) y quien ya tuvo una oferta en los últimos
 * 12 meses.
 */
export function sinOfertaDeRetencionPor(
  tenant: TenantParaOfertaDeBaja,
  extra: { ultimaOfertaAt?: string | null; cuponEnStripe?: boolean } = {},
  ahora: number = Date.now(),
): SinOfertaPor | null {
  if (tenant.plan === 'cortesia') return 'cortesia';
  if (!tenant.stripeSubscriptionId || (tenant.subscriptionStatus !== 'active' && tenant.subscriptionStatus !== 'past_due')) return 'sin_suscripcion';
  if (tenant.subscriptionStatus === 'past_due') return 'impago';
  if (tenant.billingInterval === 'year') return 'anual';
  if (tenant.plan === 'founder') return 'founder';
  if (tenant.plan !== 'basic' && tenant.plan !== 'pro') return 'sin_suscripcion';
  if (pausaPendiente(tenant, ahora)) return 'en_pausa';
  if (tenant.cancelAt && Date.parse(tenant.cancelAt) > ahora) return 'baja_programada';
  if (extra.cuponEnStripe || ofertaVigente(tenant, ahora)) return 'oferta_activa';
  if (extra.ultimaOfertaAt && ofertaReciente(extra.ultimaOfertaAt, ahora)) return 'ya_tuvo_oferta';
  return null;
}

/** ¿Esa oferta fue hace menos de 12 meses? */
export function ofertaReciente(fecha: string, ahora: number = Date.now()): boolean {
  const t = Date.parse(fecha);
  return Number.isFinite(t) && ahora - t < DIAS_ENTRE_OFERTAS_DE_RETENCION * 86_400_000;
}

/** Fin de la pausa: el fin del periodo pagado + N meses (mismo día; si no existe, el último del mes). */
export function finDePausa(periodoHasta: string, meses: MesesDePausa): string {
  const d = new Date(periodoHasta);
  const dia = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + meses);
  const ultimo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dia, ultimo));
  return d.toISOString();
}

/** Lo que pagaría con el 50 %: sale del MRR real del tenant (plan + extras), sin impuestos. */
export function precioConDescuentoDeRetencion(mrr: number): number {
  return Math.round(mrr * (1 - RETENCION_DESCUENTO.percent / 100) * 100) / 100;
}

/** El texto prellenado de WhatsApp de «Te la dejamos montada». */
export function mensajeWhatsappAyuda(restaurante: string): string {
  return `Hola, soy de ${restaurante}. Quiero que me ayudéis a dejar Alexia montada en una llamada de 20 minutos.`;
}

/* ------------------------------------------------------------------ */
/* Lo que se apunta de cada flujo (para /admin)                         */
/* ------------------------------------------------------------------ */

/** Una vez que abrió «Cancelar mi plan». Va en `tenant.retencionBaja` (las 20 últimas). */
export interface FlujoRetencion {
  id: string;
  abiertoAt: string;
  motivo?: CancellationReason;
  /** La oferta enseñada; null = ninguna (ver `sinOfertaPor`). */
  oferta?: OfertaRetencion | null;
  sinOfertaPor?: SinOfertaPor;
  aceptada?: AceptacionRetencion;
  aceptadaAt?: string;
  mesesDePausa?: MesesDePausa;
  rechazadaAt?: string;
  /** Pulsó «Cancelar mi plan» en la confirmación y se fue al portal de Stripe. */
  aPortalAt?: string;
}

/** Añade o actualiza un flujo, sin pasar de 20. */
export function conFlujoDeRetencion(lista: FlujoRetencion[] | undefined, flujo: FlujoRetencion): FlujoRetencion[] {
  const previa = lista ?? [];
  const i = previa.findIndex((f) => f.id === flujo.id);
  const siguiente = i >= 0 ? previa.map((f, j) => (j === i ? { ...f, ...flujo } : f)) : [...previa, flujo];
  return siguiente.slice(-20);
}

export interface ResumenRetencionBaja {
  /** Inicio de la ventana (90 días). */
  desde: string;
  abiertos: number;
  conOferta: number;
  ofrecidas: Record<OfertaRetencion, number>;
  aceptadas: Record<AceptacionRetencion, number>;
  /** Pulsaron «Cancelar mi plan» al final del flujo. */
  alPortal: number;
  /** De los flujos de la ventana, cuántos acabaron con la baja (consumada o programada). */
  cancelaron: number;
  /** Aceptaron una oferta hace 90 días o más (en cualquier fecha). */
  aceptaron90: number;
  /** De esos, cuántos siguen pagando hoy. */
  siguen90: number;
}

interface FilaParaRetencion {
  internal?: boolean;
  subscriptionStatus: string;
  canceledAt?: string;
  cancelAt?: string;
  retencionBaja?: FlujoRetencion[];
}

/** Lo que enseña /admin junto al churn. Las cuentas internas no cuentan. */
export function resumenRetencionBaja(rows: FilaParaRetencion[], ahora: number = Date.now()): ResumenRetencionBaja {
  const desde = new Date(ahora - 90 * 86_400_000).toISOString();
  const r: ResumenRetencionBaja = {
    desde,
    abiertos: 0,
    conOferta: 0,
    ofrecidas: { descuento: 0, pausa: 0, ayuda: 0, falta: 0 },
    aceptadas: { descuento: 0, basico: 0, pausa: 0, ayuda: 0 },
    alPortal: 0,
    cancelaron: 0,
    aceptaron90: 0,
    siguen90: 0,
  };
  const hace90 = ahora - 90 * 86_400_000;
  for (const row of rows) {
    if (row.internal) continue;
    for (const f of row.retencionBaja ?? []) {
      if (f.aceptada && f.aceptadaAt && Date.parse(f.aceptadaAt) <= hace90) {
        r.aceptaron90 += 1;
        const seFue = Boolean(row.canceledAt && row.canceledAt >= f.aceptadaAt);
        if (!seFue && (row.subscriptionStatus === 'active' || row.subscriptionStatus === 'past_due')) r.siguen90 += 1;
      }
      if (f.abiertoAt < desde) continue;
      r.abiertos += 1;
      if (f.oferta) {
        r.conOferta += 1;
        r.ofrecidas[f.oferta] += 1;
      }
      if (f.aceptada) r.aceptadas[f.aceptada] += 1;
      if (f.aPortalAt) r.alPortal += 1;
      const consumada = Boolean(row.canceledAt && row.canceledAt >= f.abiertoAt);
      const programada = Boolean(row.cancelAt && f.aPortalAt);
      if (consumada || programada) r.cancelaron += 1;
    }
  }
  return r;
}

/* ------------------------------------------------------------------ */
/* Lo que se cuentan la API (o la demo) y la web                        */
/* ------------------------------------------------------------------ */

/** Respuesta al abrir «Cancelar mi plan». */
export interface InicioRetencion {
  flujoId: string;
  /** Fin del periodo pagado (ISO): hasta ahí lo mantiene si cancela. */
  periodoHasta: string | null;
  anual: boolean;
  /** null = puede recibir una oferta; cuál, lo decide el motivo. */
  sinOfertaPor: SinOfertaPor | null;
}

/** La oferta de la pantalla 2, con sus cifras (sin impuestos, en su moneda de cobro). */
export interface OfertaRetencionVista {
  tipo: OfertaRetencion;
  aceptaciones: AceptacionRetencion[];
  /** Lo que paga hoy al mes (plan + extras). */
  precioActual: number;
  /** Con el 50 % durante 3 meses. */
  precioConDescuento?: number;
  /** Si pasa a Básico (con sus extras). */
  precioBasico?: number;
  /** Desde cuándo y hasta cuándo, según los meses de pausa. */
  pausaDesde?: string;
  pausaHasta?: Record<MesesDePausa, string>;
}

export interface MotivoRetencionRespuesta {
  oferta: OfertaRetencionVista | null;
  sinOfertaPor: SinOfertaPor | null;
}

export interface AceptarRetencionEntrada {
  flujoId: string;
  aceptada: AceptacionRetencion;
  meses?: MesesDePausa;
  /** «¿Qué te falta?» (motivo `missing`). */
  falta?: string;
}

export interface AceptarRetencionRespuesta {
  ok: true;
  aceptada: AceptacionRetencion;
  pausedFrom?: string;
  pausedUntil?: string;
}

/** Arma la oferta que se enseña. `mrrDe` es `mrrDeTenant` (sin pausa) para no importar en círculo. */
export function vistaDeOfertaDeRetencion(
  tipo: OfertaRetencion,
  tenant: { plan: PlanId },
  precios: { actual: number; basico: number },
  periodoHasta: string | null,
  ahora: number = Date.now(),
): OfertaRetencionVista {
  const aceptaciones = aceptacionesDe(tipo, tenant.plan);
  const desde = periodoHasta ?? new Date(ahora).toISOString();
  return {
    tipo,
    aceptaciones,
    precioActual: precios.actual,
    ...(tipo === 'descuento' ? { precioConDescuento: precioConDescuentoDeRetencion(precios.actual) } : {}),
    ...(aceptaciones.includes('basico') ? { precioBasico: precios.basico } : {}),
    ...(aceptaciones.includes('pausa') ? { pausaDesde: desde, pausaHasta: { 1: finDePausa(desde, 1), 2: finDePausa(desde, 2) } } : {}),
  };
}
